"use client";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle } from "lucide-react";
import { DataTableRowActions } from "./news-notices.actions";
import { DataTableColumnHeader } from "./news-notices.header";

type NewsNotice = {
  id: string;
  noticeType: string;
  title: string;
  description: string;
  author: string | null;
  category: string | null;
  tags: string[];
  priority: string;
  status: string;
  date: Date;
  expirationDate: Date | null;
  featuredImageId: string | null;
  attachmentId: string | null;
  organizationId: string;
  createdAt: Date;
  updatedAt: Date;
};

export const columns: ColumnDef<NewsNotice>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "noticeType",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    cell: ({ row }) => {
      const type = row.getValue("noticeType") as string;
      return (
        <Badge variant={type === "news" ? "default" : "secondary"}>
          {type === "news" ? "News" : "Notice"}
        </Badge>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: "title",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Title" />
    ),
    cell: ({ row }) => (
      <div className="max-w-[300px] font-medium">{row.getValue("title")}</div>
    ),
    enableSorting: true,
    enableHiding: true,
    filterFn: (row, _id, value) => {
      return row.original.title.toLowerCase().includes(value.toLowerCase());
    },
  },
  {
    accessorKey: "category",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Category" />
    ),
    cell: ({ row }) => {
      const category = row.original.category;
      return <div className="max-w-[150px] truncate">{category || "—"}</div>;
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: "priority",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Priority" />
    ),
    cell: ({ row }) => {
      const priority = row.getValue("priority") as string;
      const variants: Record<string, "default" | "secondary" | "destructive"> =
        {
          low: "secondary",
          normal: "default",
          high: "default",
          urgent: "destructive",
        };
      return (
        <Badge variant={variants[priority] || "default"} className="capitalize">
          {priority}
        </Badge>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: "date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("date") as Date;
      return <div>{new Date(date).toLocaleDateString()}</div>;
    },
    enableSorting: true,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      const variants: Record<string, "default" | "secondary" | "outline"> = {
        draft: "outline",
        published: "default",
        archived: "secondary",
      };
      return (
        <Badge variant={variants[status] || "default"} className="capitalize">
          {status}
        </Badge>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: "expirationDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Expiration" />
    ),
    cell: ({ row }) => {
      const expirationDate = row.original.expirationDate;
      if (!expirationDate) return <div>—</div>;

      const isExpired = new Date(expirationDate) < new Date();
      const isExpiringSoon =
        new Date(expirationDate) <
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

      return (
        <div className="flex items-center gap-2">
          {(isExpired || isExpiringSoon) && (
            <AlertCircle
              className={`h-4 w-4 ${isExpired ? "text-destructive" : "text-yellow-500"}`}
            />
          )}
          <span className={isExpired ? "text-destructive" : ""}>
            {new Date(expirationDate).toLocaleDateString()}
          </span>
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: "actions",
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
];
